import React, { useState } from 'react';
import { Plus, X, BookOpen, GraduationCap, Tags } from 'lucide-react';

const QuestionCategoryForm = ({ value, onChange, availableCategories }) => {
  const [showSubjectInput, setShowSubjectInput] = useState(false);
  const [showGradeInput, setShowGradeInput] = useState(false);
  const [showTopicInput, setShowTopicInput] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newGrade, setNewGrade] = useState('');
  const [newTopic, setNewTopic] = useState('');

  const handleSubjectAdd = () => {
    if (newSubject.trim()) {
      onChange({ ...value, subject: newSubject.trim() });
      setNewSubject('');
      setShowSubjectInput(false);
    }
  };

  const handleGradeAdd = () => {
    if (newGrade.trim()) {
      onChange({ ...value, grade: newGrade.trim() });
      setNewGrade('');
      setShowGradeInput(false);
    }
  };

  const handleTopicAdd = () => {
    if (newTopic.trim()) {
      const topics = value.topics || [];
      if (!topics.includes(newTopic.trim())) {
        onChange({ ...value, topics: [...topics, newTopic.trim()] });
      }
      setNewTopic('');
      setShowTopicInput(false);
    }
  };

  const handleTopicRemove = (topic) => {
    const topics = value.topics || [];
    onChange({ ...value, topics: topics.filter(t => t !== topic) });
  };

  return (
    <div className="space-y-4 bg-slate-700 p-4 rounded-lg">
      <h4 className="font-semibold text-slate-200 flex items-center gap-2">
        <Tags className="h-4 w-4" />
        Question Categories
      </h4>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          <BookOpen className="h-4 w-4 inline mr-1" />
          Subject
        </label>
        {value.subject ? (
          <div className="flex items-center gap-2">
            <span className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">
              {value.subject}
            </span>
            <button
              type="button"
              onClick={() => onChange({ ...value, subject: null })}
              className="p-1 hover:bg-slate-600 rounded text-red-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : showSubjectInput ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubjectAdd()}
              placeholder="Enter subject name"
              className="flex-1 bg-slate-600 border border-slate-500 rounded-md px-3 py-2 text-slate-100"
              autoFocus
            />
            <button
              type="button"
              onClick={handleSubjectAdd}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowSubjectInput(false)}
              className="px-4 py-2 bg-slate-600 text-slate-300 rounded-md hover:bg-slate-500"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableCategories.subjects.map(subject => (
              <button
                key={subject}
                type="button"
                onClick={() => onChange({ ...value, subject })}
                className="px-3 py-2 bg-slate-600 text-slate-300 rounded-lg text-sm hover:bg-indigo-600 hover:text-white transition-colors"
              >
                {subject}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowSubjectInput(true)}
              className="px-3 py-2 bg-slate-600 text-slate-300 rounded-lg text-sm hover:bg-green-600 hover:text-white transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              New Subject
            </button>
          </div>
        )}
      </div>

      {/* Grade */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          <GraduationCap className="h-4 w-4 inline mr-1" />
          Grade
        </label>
        {value.grade ? (
          <div className="flex items-center gap-2">
            <span className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">
              {value.grade}
            </span>
            <button
              type="button"
              onClick={() => onChange({ ...value, grade: null })}
              className="p-1 hover:bg-slate-600 rounded text-red-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : showGradeInput ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={newGrade}
              onChange={(e) => setNewGrade(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleGradeAdd()}
              placeholder="e.g., Grade 10"
              className="flex-1 bg-slate-600 border border-slate-500 rounded-md px-3 py-2 text-slate-100"
              autoFocus
            />
            <button
              type="button"
              onClick={handleGradeAdd}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowGradeInput(false)}
              className="px-4 py-2 bg-slate-600 text-slate-300 rounded-md hover:bg-slate-500"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableCategories.grades.map(grade => (
              <button
                key={grade}
                type="button"
                onClick={() => onChange({ ...value, grade })}
                className="px-3 py-2 bg-slate-600 text-slate-300 rounded-lg text-sm hover:bg-indigo-600 hover:text-white transition-colors"
              >
                {grade}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowGradeInput(true)}
              className="px-3 py-2 bg-slate-600 text-slate-300 rounded-lg text-sm hover:bg-green-600 hover:text-white transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              New Grade
            </button>
          </div>
        )}
      </div>

      {/* Topics */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          <Tags className="h-4 w-4 inline mr-1" />
          Topics (multiple allowed)
        </label>
        {(value.topics && value.topics.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-2">
            {value.topics.map(topic => (
              <span
                key={topic}
                className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm flex items-center gap-2"
              >
                {topic}
                <button
                  type="button"
                  onClick={() => handleTopicRemove(topic)}
                  className="hover:bg-green-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {showTopicInput ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTopicAdd()}
              placeholder="Enter topic name"
              className="flex-1 bg-slate-600 border border-slate-500 rounded-md px-3 py-2 text-slate-100"
              autoFocus
            />
            <button
              type="button"
              onClick={handleTopicAdd}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowTopicInput(false)}
              className="px-4 py-2 bg-slate-600 text-slate-300 rounded-md hover:bg-slate-500"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableCategories.topics.map(topic => (
              <button
                key={topic}
                type="button"
                onClick={() => {
                  const topics = value.topics || [];
                  if (!topics.includes(topic)) {
                    onChange({ ...value, topics: [...topics, topic] });
                  }
                }}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  value.topics?.includes(topic)
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-600 text-slate-300 hover:bg-green-600 hover:text-white'
                }`}
              >
                {topic}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowTopicInput(true)}
              className="px-3 py-2 bg-slate-600 text-slate-300 rounded-lg text-sm hover:bg-green-600 hover:text-white transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              New Topic
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCategoryForm;